/* eslint-disable react/jsx-no-literals */
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { PRIVACY_FRAGMENT } from './constants';
import { inlineRichText, mailtoRichText } from './rich-text-values';

interface PolicySectionProps {
  accountName: string;
  supportContact: string;
}

export function PolicySection({
  accountName,
  supportContact,
}: PolicySectionProps) {
  const richText = mailtoRichText(supportContact);

  return (
    <section
      id={PRIVACY_FRAGMENT}
      aria-labelledby={`${PRIVACY_FRAGMENT}-heading`}
      data-testid="privacy-policy-section"
      className="mb-12"
    >
      <h2
        id={`${PRIVACY_FRAGMENT}-heading`}
        className="mb-4 text-2xl font-semibold"
      >
        <FormattedMessage
          defaultMessage="Privacy Policy"
          description="Heading for the Privacy Policy section of the career site privacy page"
        />
      </h2>

      <h3>
        <FormattedMessage
          defaultMessage="1. Who We Are"
          description="Privacy policy section 1 heading: Who We Are"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="This Privacy Policy describes how <strong>{accountName}</strong> (“we,” “us,” or “our”) collects, uses, and protects your personal information when you apply for a position through our career site."
          description="Privacy policy section 1 intro paragraph describing scope of the policy"
          values={{ accountName, ...inlineRichText }}
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="Our career site is powered by <strong>Fountain, Inc.</strong> (“Fountain”), a workforce management platform that processes your personal information on our behalf as our service provider. Fountain acts as a data processor; we are the data controller responsible for determining how and why your personal information is processed."
          description="Privacy policy section 1 paragraph explaining Fountain's role as data processor"
          values={inlineRichText}
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="2. Information We Collect"
          description="Privacy policy section 2 heading: Information We Collect"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="We may collect the following categories of personal information when you interact with our career site:"
          description="Privacy policy section 2 intro paragraph"
        />
      </p>
      <h4>
        <FormattedMessage
          defaultMessage="Information you provide directly:"
          description="Privacy policy section 2 subheading for directly-provided info"
        />
      </h4>
      <ul>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Identifiers:</strong> Name, email address, phone number, mailing address"
            description="Privacy policy section 2 list item: identifiers"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Employment information:</strong> Work history, education, skills, certifications, and responses to application questions"
            description="Privacy policy section 2 list item: employment information"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Documents:</strong> Resume, government-issued identification (where required for employment verification), and other documents you upload during the application or onboarding process"
            description="Privacy policy section 2 list item: documents"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Sensitive personal information:</strong> Depending on the position and applicable law, we may collect government-issued identification numbers (such as Social Security Number or driver's license number) for employment verification or background check purposes. We process sensitive personal information only for the specific purposes permitted by law and do not use it for profiling or advertising. California residents have the right to limit our use of sensitive personal information — see Section 6."
            description="Privacy policy section 2 list item: sensitive personal information"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>SMS consent records:</strong> If you opt in to receive text messages, we collect and retain records of your consent choices"
            description="Privacy policy section 2 list item: SMS consent records"
            values={inlineRichText}
          />
        </li>
      </ul>
      <h4>
        <FormattedMessage
          defaultMessage="Information collected automatically through our platform provider:"
          description="Privacy policy section 2 subheading for automatically-collected info"
        />
      </h4>
      <p>
        <FormattedMessage
          defaultMessage="When you visit our career site, our platform provider (Fountain) automatically collects certain information using cookies and similar technologies on our behalf. This may include:"
          description="Privacy policy section 2 intro for automatically-collected info"
        />
      </p>
      <ul>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Device and usage data:</strong> IP address, browser type, device identifiers, operating system, and approximate location derived from your IP address"
            description="Privacy policy section 2 list item: device and usage data"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Interaction data:</strong> Pages visited on our career site, time spent, and actions taken within the application process"
            description="Privacy policy section 2 list item: interaction data"
            values={inlineRichText}
          />
        </li>
      </ul>
      <p>
        <FormattedMessage
          defaultMessage="We collect personal information directly from you (via the application form) and automatically through our platform provider's technology (via cookies and similar technologies embedded in our career site)."
          description="Privacy policy section 2 closing paragraph summarizing collection methods"
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="3. How We Use Your Information"
          description="Privacy policy section 3 heading: How We Use Your Information"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="We use your personal information for the following purposes:"
          description="Privacy policy section 3 intro paragraph"
        />
      </p>
      <ul>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Processing your application:</strong> Evaluating your qualifications, scheduling interviews, conducting background checks (with your separate consent where required), and communicating with you about your application status"
            description="Privacy policy section 3 use: processing application"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Onboarding:</strong> If you are offered a position, facilitating the onboarding process including document collection, training scheduling, and orientation"
            description="Privacy policy section 3 use: onboarding"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Transactional communications:</strong> Sending application status updates, interview reminders, onboarding instructions, and other communications directly related to your application or employment (via SMS, email, or phone, based on your consent choices)"
            description="Privacy policy section 3 use: transactional communications"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Marketing communications:</strong> If you separately opt in, sending information about other job opportunities you may be eligible for"
            description="Privacy policy section 3 use: marketing communications"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Legal compliance:</strong> Fulfilling our legal obligations, including record-keeping, tax reporting, employment eligibility verification, and responding to lawful requests from government authorities"
            description="Privacy policy section 3 use: legal compliance"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Platform operations:</strong> We authorize our platform provider (Fountain) to maintain the security and functionality of our career site on our behalf, including detecting fraud and resolving technical issues"
            description="Privacy policy section 3 use: platform operations"
            values={inlineRichText}
          />
        </li>
      </ul>

      <h3>
        <FormattedMessage
          defaultMessage="4. SMS and Calls Communications Program"
          description="Privacy policy section 4 heading: SMS and Calls Communications Program"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="<strong>Consent to receive text messages or calls is entirely voluntary and is not a condition of submitting a job application or obtaining employment.</strong> If you choose not to opt in, we may contact you via email instead."
          description="Privacy policy section 4 voluntariness statement"
          values={inlineRichText}
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="<strong>No mobile information — including mobile phone numbers, SMS opt-in status, and consent data — will be shared, sold, or rented to third parties or affiliates for their marketing or promotional purposes.</strong> Mobile opt-in and consent information is shared only with communications service providers (e.g., carriers and messaging platform vendors) strictly as necessary to deliver the messages you have requested, and not for any third party's independent marketing use."
          description="Privacy policy section 4 statement about not sharing mobile info"
          values={inlineRichText}
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="During the application process, you may be asked to provide separate consent for each of the following communication types:"
          description="Privacy policy section 4 lead-in to communication-types list"
        />
      </p>
      <ul>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Application-related SMS:</strong> Status updates, interview reminders, onboarding instructions, and other messages directly related to your application or employment with {accountName}. <em>(Transactional)</em>"
            description="Privacy policy section 4 communication type: application SMS"
            values={{ accountName, ...inlineRichText }}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Job opportunity SMS:</strong> Information about other positions with {accountName} that you may be eligible for. <em>(Marketing — requires separate opt-in)</em>"
            description="Privacy policy section 4 communication type: marketing SMS"
            values={{ accountName, ...inlineRichText }}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Application-related calls:</strong> Phone calls regarding your application status, interview scheduling, and onboarding. <em>(Transactional)</em>"
            description="Privacy policy section 4 communication type: application calls"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Job opportunity calls:</strong> Phone calls about other positions with {accountName} that you may be eligible for. <em>(Marketing — requires separate opt-in)</em>"
            description="Privacy policy section 4 communication type: marketing calls"
            values={{ accountName, ...inlineRichText }}
          />
        </li>
      </ul>
      <p>
        <FormattedMessage
          defaultMessage="Each consent choice is <strong>independent</strong>. You may opt in to any, all, or none of these communication types. Your choices will not affect your application."
          description="Privacy policy section 4 explanation that consent choices are independent"
          values={inlineRichText}
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="<strong>Message frequency:</strong> Message frequency varies based on your application status and activity. Standard message and data rates may apply."
          description="Privacy policy section 4 message frequency statement"
          values={inlineRichText}
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="<strong>How to get help:</strong> Reply <strong>HELP</strong> to any text message for assistance, or contact us at <supportLink>{supportContact}</supportLink>."
          description="Privacy policy section 4 help instructions"
          values={{ supportContact, ...richText }}
        />
      </p>
      <h4>
        <FormattedMessage
          defaultMessage="How to opt out:"
          description="Privacy policy section 4 opt-out subheading"
        />
      </h4>
      <ul>
        <li>
          <FormattedMessage
            defaultMessage="<strong>SMS:</strong> Reply <strong>STOP</strong> to any text message. Replying STOP will opt you out of <strong>all SMS message categories</strong> from that number. If you wish to opt out of only one category (e.g., marketing SMS but not transactional SMS), contact us at <supportLink>{supportContact}</supportLink> to update your preferences granularly."
            description="Privacy policy section 4 opt-out instructions: SMS"
            values={{ supportContact, ...richText }}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Calls:</strong> Inform the caller that you wish to be removed, or contact us at <supportLink>{supportContact}</supportLink>."
            description="Privacy policy section 4 opt-out instructions: calls"
            values={{ supportContact, ...richText }}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>All channels:</strong> Contact <supportLink>{supportContact}</supportLink> to update or revoke your consent preferences across all communication channels."
            description="Privacy policy section 4 opt-out instructions: all channels"
            values={{ supportContact, ...richText }}
          />
        </li>
      </ul>
      <p>
        <FormattedMessage
          defaultMessage="When you opt out, we will process your request promptly. Opting out of marketing messages will not affect transactional messages related to an active application or employment relationship, unless you opt out of those separately."
          description="Privacy policy section 4 closing paragraph about opt-out scope"
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="5. How We Share Your Information"
          description="Privacy policy section 5 heading: How We Share Your Information"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="<strong>We do not sell your personal information. We do not share your personal information for cross-context behavioral advertising.</strong>"
          description="Privacy policy section 5 declaration that we don't sell info"
          values={inlineRichText}
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="<strong>No mobile information — including mobile phone numbers and SMS opt-in or consent data — will be shared with third parties or affiliates for their marketing or promotional purposes.</strong> All other categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties."
          description="Privacy policy section 5 statement about not sharing mobile info"
          values={inlineRichText}
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="When you submit an application through our career site, your personal information is collected directly by our platform provider (Fountain) on our behalf. We may also share your personal information with the following categories of service providers who assist us in operating our hiring process:"
          description="Privacy policy section 5 lead-in to service-provider list"
        />
      </p>
      <ul>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Fountain, Inc.</strong> — Our hiring platform provider. Fountain collects and processes your personal information on our behalf to facilitate the application process, deliver SMS and other communications, and manage hiring workflows. Fountain's privacy policy is available at <fountainPrivacyLink>fountain.com/privacy</fountainPrivacyLink>."
            description="Privacy policy section 5 service provider: Fountain"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Communications service providers</strong> — Phone carriers and messaging service providers as necessary to deliver text messages and phone calls. We may share your personal data, including your SMS opt-in or consent status, with third parties that help us provide our messaging services, including but not limited to platform providers, phone companies, and any other vendors who assist in the delivery of text messages. This sharing is strictly for service delivery purposes."
            description="Privacy policy section 5 service provider: communications providers"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Background check providers</strong> — If you advance in the hiring process and provide separate consent, we may share information with third-party providers to conduct background checks."
            description="Privacy policy section 5 service provider: background check"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Government authorities</strong> — Where required by law, regulation, or legal process."
            description="Privacy policy section 5 service provider: government authorities"
            values={inlineRichText}
          />
        </li>
      </ul>
      <p>
        <FormattedMessage
          defaultMessage="All service providers are contractually required to protect your information, use it only for the purposes specified by us, and not retain it beyond what is necessary to provide their services."
          description="Privacy policy section 5 closing paragraph about service provider obligations"
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="6. Your Rights and Choices"
          description="Privacy policy section 6 heading: Your Rights and Choices"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="Depending on your location, you may have the following rights regarding your personal information:"
          description="Privacy policy section 6 lead-in to rights list"
        />
      </p>
      <ul>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Right to know:</strong> You may request information about the categories and specific pieces of personal information we have collected about you, the sources of that information, the purposes for collection, and the categories of third parties with whom we have shared it."
            description="Privacy policy section 6 right: right to know"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Right to access:</strong> You may request a copy of the personal information we hold about you."
            description="Privacy policy section 6 right: right to access"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Right to correction:</strong> You may request that we correct inaccurate personal information."
            description="Privacy policy section 6 right: right to correction"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Right to deletion:</strong> You may request that we delete your personal information, subject to certain legal exceptions (such as record-keeping obligations)."
            description="Privacy policy section 6 right: right to deletion"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Right to opt out of sale/sharing:</strong> We do not sell or share your personal information for cross-context behavioral advertising. No opt-out is necessary because we do not engage in these practices."
            description="Privacy policy section 6 right: right to opt out of sale/sharing"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Right to limit use of sensitive personal information:</strong> If we collect sensitive personal information (such as Social Security Number or government-issued ID), you have the right to limit our use of that information to purposes that are necessary to provide the services you reasonably expect. To exercise this right, contact us at <supportLink>{supportContact}</supportLink>."
            description="Privacy policy section 6 right: limit use of sensitive PI"
            values={{ supportContact, ...richText }}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Right to non-discrimination:</strong> We will not discriminate against you for exercising any of your privacy rights."
            description="Privacy policy section 6 right: non-discrimination"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Authorized agents:</strong> You may designate an authorized agent to submit privacy requests on your behalf. The agent must provide proof of your authorization (such as a signed written permission or power of attorney), and we may still require you to verify your identity directly."
            description="Privacy policy section 6 right: authorized agents"
            values={inlineRichText}
          />
        </li>
      </ul>
      <p>
        <FormattedMessage
          defaultMessage="<strong>California residents:</strong> Under the California Consumer Privacy Act, as amended by the California Privacy Rights Act (CCPA/CPRA), you have all of the rights listed above. To submit a verifiable consumer request, contact us at <supportLink>{supportContact}</supportLink>. We will verify your identity before fulfilling your request and respond within 45 days as required by law."
          description="Privacy policy section 6 California residents notice"
          values={{ supportContact, ...richText }}
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="Because your personal information is processed through our platform provider (Fountain), we may coordinate with Fountain to fulfill your request. This does not affect your right to submit requests directly to us, and we remain responsible for responding to you within the timeframes required by applicable law."
          description="Privacy policy section 6 closing paragraph about Fountain coordination"
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="7. Data Retention"
          description="Privacy policy section 7 heading: Data Retention"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="We retain your personal information for as long as reasonably necessary to fulfill the purposes for which it was collected, including to provide the services you have requested, process and evaluate your application, support any resulting employment relationship, comply with our legal and regulatory obligations, and resolve disputes."
          description="Privacy policy section 7 retention principle"
        />
      </p>
      <ul>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Application data:</strong> If you are hired, your application data is retained for the duration of the employment relationship and for a reasonable period thereafter as required by applicable employment, tax, and record-keeping laws. If you are not hired, your application data is typically retained for a specific duration to allow for consideration for future opportunities and to comply with legal and regulatory requirements, unless a shorter or longer retention period is required by law."
            description="Privacy policy section 7 retention: application data"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>SMS consent records:</strong> Retained for a minimum of five (5) years for regulatory compliance purposes, consistent with carrier and FCC record-keeping expectations."
            description="Privacy policy section 7 retention: SMS consent records"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Automatically collected data:</strong> Retained in accordance with our platform provider's standard retention policies."
            description="Privacy policy section 7 retention: automatic data"
            values={inlineRichText}
          />
        </li>
      </ul>
      <p>
        <FormattedMessage
          defaultMessage="Retention periods are subject to the capabilities and configurations of our platform provider (Fountain). If you have questions about specific retention timelines, contact us at <supportLink>{supportContact}</supportLink>."
          description="Privacy policy section 7 contact for retention questions"
          values={{ supportContact, ...richText }}
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="Once your personal information is no longer needed for any of the purposes described above, we will coordinate with our platform provider to securely delete or de-identify it in accordance with applicable data retention and disposal procedures."
          description="Privacy policy section 7 closing paragraph about deletion"
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="8. For EU/EEA/UK Applicants: International Data Transfers"
          description="Privacy policy section 8 heading: International Data Transfers"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="Your personal information may be transferred to and processed in the United States, where our platform provider Fountain operates its primary infrastructure. The United States may not provide the same level of data protection as your home country."
          description="Privacy policy section 8 description of US transfers"
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="Where we transfer personal information from the EU/EEA or UK to the United States, we rely on the following safeguards: (a) the EU-U.S. Data Privacy Framework (DPF), where applicable; and/or (b) Standard Contractual Clauses (SCCs) approved by the European Commission, incorporated into our data processing agreements with service providers. You may request a copy of the applicable safeguards by contacting us at <supportLink>{supportContact}</supportLink>."
          description="Privacy policy section 8 description of safeguards"
          values={{ supportContact, ...richText }}
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="9. Cookies, Tracking Technologies, and Do Not Track"
          description="Privacy policy section 9 heading: Cookies and tracking"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="Our career site, which is operated through our platform provider (Fountain), uses cookies and similar technologies to collect the device and usage data described in Section 2. These technologies are deployed by Fountain on our behalf to operate the career site, analyze usage, and improve performance. For detailed information about specific cookies placed on our career site, please refer to Fountain's privacy policy at <fountainPrivacyLink>fountain.com/privacy</fountainPrivacyLink>."
          description="Privacy policy section 9 description of cookies"
          values={inlineRichText}
        />
      </p>
      <h4>
        <FormattedMessage
          defaultMessage="Types of cookies used:"
          description="Privacy policy section 9 subheading for cookie types"
        />
      </h4>
      <ul>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Strictly necessary cookies:</strong> Required for the career site to function (e.g., session management, security). These cannot be disabled."
            description="Privacy policy section 9 cookie type: strictly necessary"
            values={inlineRichText}
          />
        </li>
        <li>
          <FormattedMessage
            defaultMessage="<strong>Analytics cookies:</strong> Help us understand how applicants interact with the career site. These may be provided by third-party analytics services."
            description="Privacy policy section 9 cookie type: analytics"
            values={inlineRichText}
          />
        </li>
      </ul>
      <p>
        <FormattedMessage
          defaultMessage="<strong>EU/EEA/UK applicants:</strong> Where required by the ePrivacy Directive (Directive 2002/58/EC) or the UK Privacy and Electronic Communications Regulations (PECR), we will obtain your consent before placing non-essential cookies. You may manage your cookie preferences through your browser settings."
          description="Privacy policy section 9 EU/EEA/UK consent statement"
          values={inlineRichText}
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="<strong>Do Not Track signals:</strong> Our career site does not currently respond to “Do Not Track” (DNT) browser signals. There is no uniform industry standard for recognizing or honoring DNT signals at this time."
          description="Privacy policy section 9 Do Not Track statement"
          values={inlineRichText}
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="10. Data Security"
          description="Privacy policy section 10 heading: Data Security"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="We take reasonable measures to ensure your personal information is protected. Because your data is processed through our platform provider (Fountain), we rely on Fountain's technical and organizational security controls, which include SOC 2 Type II certification, ISO 27001:2022 certification, and encryption of data in transit (TLS 1.2+) and at rest (AES-256). We have entered into a data processing agreement with Fountain that requires Fountain to implement appropriate security measures to protect your personal information."
          description="Privacy policy section 10 security measures description"
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="No method of transmission or storage is completely secure. While we strive to protect your personal information, we cannot guarantee absolute security."
          description="Privacy policy section 10 security disclaimer"
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="11. Children's Privacy"
          description="Privacy policy section 11 heading: Children's Privacy"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="Our career site is not directed to individuals under the age of 16. We do not knowingly collect personal information from children under 16. If you believe we have collected information from a child, please contact us at <supportLink>{supportContact}</supportLink> so we can promptly delete it."
          description="Privacy policy section 11 children's privacy statement"
          values={{ supportContact, ...richText }}
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="12. Changes to This Privacy Policy"
          description="Privacy policy section 12 heading: Changes to This Privacy Policy"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="We may update this Privacy Policy from time to time. If we make material changes to how we handle your personal information, we will: (a) post a prominent notice on our career site, and (b) where feasible, notify applicants with active applications via the contact information on file. The updated policy will indicate the revised effective date."
          description="Privacy policy section 12 change notification process"
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="We encourage you to review this policy periodically. Your continued use of our career site after changes are posted does not, by itself, constitute consent to the revised terms — any processing that requires your consent will be subject to separate consent mechanisms."
          description="Privacy policy section 12 closing paragraph"
        />
      </p>

      <h3>
        <FormattedMessage
          defaultMessage="13. Contact Us"
          description="Privacy policy section 13 heading: Contact Us"
        />
      </h3>
      <p>
        <FormattedMessage
          defaultMessage="If you have questions about this Privacy Policy or wish to exercise any of your rights, please contact us:"
          description="Privacy policy section 13 contact lead-in"
        />
      </p>
      <p>
        <strong>{accountName}</strong>
      </p>
      <p>
        <FormattedMessage
          defaultMessage="Email: <supportLink>{supportContact}</supportLink>"
          description="Privacy policy section 13 contact email"
          values={{ supportContact, ...richText }}
        />
      </p>
      <p>
        <FormattedMessage
          defaultMessage="If you have questions specifically about how the Fountain platform processes your data, you may also contact Fountain's privacy team at: <privacyMailLink>privacy@fountain.com</privacyMailLink>"
          description="Privacy policy section 13 Fountain contact"
          values={richText}
        />
      </p>
    </section>
  );
}
